import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Spinner, Text } from "@chakra-ui/react";
import { getChoices } from "../api/nova/choices";
import type { NovaChoices } from "../api/nova/types";

interface ChoicesContextValue {
  choices: NovaChoices;
  getLabel: (
    category: keyof NovaChoices,
    value: number | string | null,
  ) => string;
}

const ChoicesContext = createContext<ChoicesContextValue | null>(null);

export function ChoicesProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useQuery<NovaChoices>({
    queryKey: ["choices"],
    queryFn: getChoices,
    staleTime: Infinity,
    retry: 2,
  });

  if (isLoading) {
    return (
      <Box
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="red.500">Failed to load application data. Please refresh.</Text>
      </Box>
    );
  }

  function getLabel(
    category: keyof NovaChoices,
    value: number | string | null,
  ): string {
    const list = data![category] as { value: unknown; label: string }[];
    return list.find((item) => item.value === value)?.label ?? String(value ?? "");
  }

  return (
    <ChoicesContext.Provider value={{ choices: data, getLabel }}>
      {children}
    </ChoicesContext.Provider>
  );
}

export function useChoices(): ChoicesContextValue {
  const ctx = useContext(ChoicesContext);
  if (!ctx) throw new Error("useChoices must be used within ChoicesProvider");
  return ctx;
}
